import React, { useEffect, useState } from 'react'
import { clientDatabasesAPI } from '@hpanel/web-common/api/client'
import type {
  ClientDatabase,
  ClientDatabaseUser,
  PermissionLevel,
} from '@hpanel/web-common/api/types'
import { PhpMyAdminButton } from './PhpMyAdminButton'
import { RotatePasswordModal } from './RotatePasswordModal'

/**
 * Per-database user manager: list + add + remove + change-permission
 * + rotate-password.
 *
 * UX intent:
 *   - Load users on mount via `listUsers`. While loading, render a
 *     skeleton so the modal doesn't flash empty.
 *   - Inline permission-level dropdown that fires PUT-permissions on
 *     every change. Optimistic update with rollback on error so the
 *     interaction feels instant; rollback is non-negotiable for §11.
 *   - Add-user form at the bottom — collapsible-style, always visible
 *     (so the user can find it without hunting).
 *   - Primary user can't be removed (UI-disabled with explanation).
 *
 * §9: every API call uses fetchAPI's clean errors. Errors render in
 * a per-row error span (or a top-level banner for list/add failures)
 * with the server's `detail` text verbatim.
 */

const LEVEL_OPTIONS: { value: PermissionLevel; label: string }[] = [
  { value: 'readonly', label: 'Read-only' },
  { value: 'readwrite', label: 'Read & Write' },
  { value: 'all', label: 'All Privileges' },
]

export interface ManageUsersModalProps {
  database: ClientDatabase
  onClose: () => void
}

export const ManageUsersModal: React.FC<ManageUsersModalProps> = ({
  database,
  onClose,
}) => {
  const [users, setUsers] = useState<ClientDatabaseUser[] | null>(null)
  const [listError, setListError] = useState<string | null>(null)
  const [rowErrors, setRowErrors] = useState<Record<number, string>>({})

  // Add-user form state.
  const [addSuffix, setAddSuffix] = useState('')
  const [addPassword, setAddPassword] = useState('')
  const [addLevel, setAddLevel] = useState<PermissionLevel>('readwrite')
  const [addBusy, setAddBusy] = useState(false)
  const [addError, setAddError] = useState<string | null>(null)

  // Per-row "in flight" tracker so we can disable the right controls.
  const [busyRows, setBusyRows] = useState<Record<number, boolean>>({})

  // Rotate-password modal state (per row).
  const [rotateUser, setRotateUser] = useState<ClientDatabaseUser | null>(null)

  const loadUsers = async () => {
    try {
      setListError(null)
      const res = await clientDatabasesAPI.listUsers(database.id)
      setUsers(res.users)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[ManageUsersModal] listUsers failed', e)
      setListError(e instanceof Error ? e.message : 'Could not load users')
    }
  }

  useEffect(() => {
    loadUsers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [database.id])

  const setRowBusy = (id: number, b: boolean) => {
    setBusyRows((prev) => ({ ...prev, [id]: b }))
  }

  const setRowError = (id: number, msg: string | null) => {
    setRowErrors((prev) => {
      const next = { ...prev }
      if (msg == null) delete next[id]
      else next[id] = msg
      return next
    })
  }

  const onChangePermission = async (
    user: ClientDatabaseUser,
    level: PermissionLevel,
  ) => {
    if (level === user.permission_level) return
    const previous = user.permission_level
    // Optimistic update.
    setUsers((prev) =>
      prev?.map((u) =>
        u.id === user.id ? { ...u, permission_level: level } : u,
      ) ?? prev,
    )
    setRowError(user.id, null)
    setRowBusy(user.id, true)
    try {
      const updated = await clientDatabasesAPI.updateUserPermissions(
        database.id,
        user.id,
        { permission_level: level },
      )
      // Reconcile with server's authoritative response (in case it
      // normalized something).
      setUsers((prev) =>
        prev?.map((u) => (u.id === user.id ? updated : u)) ?? prev,
      )
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[ManageUsersModal] permission update failed', e)
      // Rollback.
      setUsers((prev) =>
        prev?.map((u) =>
          u.id === user.id ? { ...u, permission_level: previous } : u,
        ) ?? prev,
      )
      setRowError(
        user.id,
        e instanceof Error ? e.message : 'Permission update failed',
      )
    } finally {
      setRowBusy(user.id, false)
    }
  }

  const onRemoveUser = async (user: ClientDatabaseUser) => {
    if (user.is_primary) return
    setRowError(user.id, null)
    setRowBusy(user.id, true)
    try {
      await clientDatabasesAPI.removeUser(database.id, user.id)
      setUsers((prev) => prev?.filter((u) => u.id !== user.id) ?? prev)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[ManageUsersModal] remove user failed', e)
      setRowError(
        user.id,
        e instanceof Error ? e.message : 'Remove failed',
      )
    } finally {
      setRowBusy(user.id, false)
    }
  }

  const onAddUser = async (e: React.FormEvent) => {
    e.preventDefault()
    if (addBusy) return
    if (!/^[a-z0-9_]{1,47}$/.test(addSuffix)) {
      setAddError(
        'Suffix must be lowercase letters, digits, or underscores (1–47 chars).',
      )
      return
    }
    if (addPassword.length < 12) {
      setAddError('Password must be at least 12 characters.')
      return
    }
    setAddBusy(true)
    setAddError(null)
    try {
      const created = await clientDatabasesAPI.addUser(database.id, {
        username_suffix: addSuffix,
        password: addPassword,
        permission_level: addLevel,
      })
      setUsers((prev) => (prev ? [...prev, created] : [created]))
      setAddSuffix('')
      setAddPassword('')
      setAddLevel('readwrite')
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[ManageUsersModal] addUser failed', e)
      setAddError(e instanceof Error ? e.message : 'Add user failed')
    } finally {
      setAddBusy(false)
    }
  }

  return (
    <>
      <div
        data-testid="manage-users-modal"
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      >
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl p-6 max-h-[90vh] flex flex-col">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Manage Users
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Database{' '}
                <span className="font-mono">{database.name}</span>
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              data-testid="manage-users-close"
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {listError && (
              <div
                data-testid="manage-users-list-error"
                className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4 text-sm text-red-700 dark:text-red-300"
              >
                {listError}
              </div>
            )}

            {users == null && !listError && (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-4">
                Loading users…
              </p>
            )}

            {users != null && users.length === 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-4">
                No users yet. Add one below.
              </p>
            )}

            {users != null && users.length > 0 && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 text-left">
                    <th className="py-2 px-2 text-gray-700 dark:text-gray-300 font-semibold">
                      Username
                    </th>
                    <th className="py-2 px-2 text-gray-700 dark:text-gray-300 font-semibold">
                      Permission
                    </th>
                    <th className="py-2 px-2 text-gray-700 dark:text-gray-300 font-semibold">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr
                      key={u.id}
                      data-testid={`db-user-row-${u.id}`}
                      className="border-b border-gray-100 dark:border-gray-800"
                    >
                      <td className="py-2 px-2 font-mono text-gray-900 dark:text-gray-100">
                        {u.username}
                        {u.is_primary && (
                          <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300">
                            primary
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-2">
                        <select
                          value={u.permission_level}
                          disabled={!!busyRows[u.id]}
                          onChange={(e) =>
                            onChangePermission(
                              u,
                              e.target.value as PermissionLevel,
                            )
                          }
                          data-testid={`change-perm-${u.id}`}
                          className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm disabled:opacity-50"
                        >
                          {LEVEL_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        {rowErrors[u.id] && (
                          <p
                            data-testid={`row-error-${u.id}`}
                            className="text-xs text-red-600 dark:text-red-400 mt-1"
                          >
                            {rowErrors[u.id]}
                          </p>
                        )}
                      </td>
                      <td className="py-2 px-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <PhpMyAdminButton
                            dbId={database.id}
                            dbUserId={u.id}
                            label="phpMyAdmin"
                            variant="inline"
                          />
                          <button
                            type="button"
                            onClick={() => setRotateUser(u)}
                            data-testid={`rotate-user-pw-${u.id}`}
                            className="text-sm text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
                            disabled={!!busyRows[u.id]}
                          >
                            Rotate Password
                          </button>
                          <button
                            type="button"
                            onClick={() => onRemoveUser(u)}
                            data-testid={`remove-user-${u.id}`}
                            className="text-sm text-red-600 dark:text-red-400 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={u.is_primary || !!busyRows[u.id]}
                            title={
                              u.is_primary
                                ? 'Primary user cannot be removed; delete the database instead.'
                                : 'Remove this user'
                            }
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Add user form */}
            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Add a user
              </h3>
              {addError && (
                <div
                  data-testid="add-user-error"
                  className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-2 mb-3 text-sm text-red-700 dark:text-red-300"
                >
                  {addError}
                </div>
              )}
              <form
                onSubmit={onAddUser}
                className="grid grid-cols-1 sm:grid-cols-3 gap-3"
              >
                <input
                  type="text"
                  required
                  pattern="[a-z0-9_]+"
                  value={addSuffix}
                  onChange={(e) => setAddSuffix(e.target.value)}
                  data-testid="add-user-suffix"
                  placeholder="username suffix"
                  className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <input
                  type="password"
                  required
                  minLength={12}
                  autoComplete="new-password"
                  value={addPassword}
                  onChange={(e) => setAddPassword(e.target.value)}
                  data-testid="add-user-password"
                  placeholder="password (min 12)"
                  className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <select
                  value={addLevel}
                  onChange={(e) =>
                    setAddLevel(e.target.value as PermissionLevel)
                  }
                  data-testid="add-user-level"
                  className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {LEVEL_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <div className="sm:col-span-3 flex justify-end">
                  <button
                    type="submit"
                    disabled={addBusy}
                    data-testid="add-user-submit"
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {addBusy ? 'Adding…' : 'Add User'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {rotateUser && (
        <RotatePasswordModal
          target={{ kind: 'user', dbId: database.id, dbUserId: rotateUser.id }}
          username={rotateUser.username}
          onClose={() => setRotateUser(null)}
          onRotated={() => {
            // No row mutation needed — we never display the password.
          }}
        />
      )}
    </>
  )
}
